"use client";

import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

export interface PaginationControlsProps {
  currentPage: number;
  onPageChange: (page: number) => void;
  pageSize?: number;
  totalCount?: number;
  totalPages?: number;
  maxVisiblePages?: number;
  className?: string;
  showIfSinglePage?: boolean;
}

export function PaginationControls({
  currentPage,
  onPageChange,
  pageSize,
  totalCount,
  totalPages: totalPagesProp,
  maxVisiblePages = 5,
  className,
  showIfSinglePage = false,
}: PaginationControlsProps) {
  const computedTotalPages = (() => {
    if (typeof totalPagesProp === "number") return totalPagesProp;
    if (typeof totalCount === "number" && typeof pageSize === "number" && pageSize > 0) {
      return Math.max(1, Math.ceil(totalCount / pageSize));
    }
    return 1;
  })();

  if (!showIfSinglePage && computedTotalPages <= 1) {
    return null;
  }

  const getPaginationItems = () => {
    const items: Array<number | "ellipsis-start" | "ellipsis-end"> = [];
    if (computedTotalPages <= maxVisiblePages) {
      for (let i = 1; i <= computedTotalPages; i++) items.push(i);
    } else {
      items.push(1);
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(computedTotalPages - 1, currentPage + 1);
      if (start > 2) items.push("ellipsis-start");
      for (let i = start; i <= end; i++) items.push(i);
      if (end < computedTotalPages - 1) items.push("ellipsis-end");
      if (computedTotalPages > 1) items.push(computedTotalPages);
    }
    return items;
  };

  return (
    <div className={className}>
      <Pagination>
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              onClick={() => onPageChange(Math.max(1, currentPage - 1))}
              className={
                currentPage === 1
                  ? "pointer-events-none opacity-50"
                  : "cursor-pointer"
              }
            />
          </PaginationItem>

          {getPaginationItems().map((item, index) => (
            <PaginationItem key={index}>
              {item === "ellipsis-start" || item === "ellipsis-end" ? (
                <PaginationEllipsis />
              ) : (
                <PaginationLink
                  onClick={() => onPageChange(item as number)}
                  isActive={currentPage === item}
                  className="cursor-pointer"
                >
                  {item}
                </PaginationLink>
              )}
            </PaginationItem>
          ))}

          <PaginationItem>
            <PaginationNext
              onClick={() => onPageChange(Math.min(computedTotalPages, currentPage + 1))}
              className={
                currentPage === computedTotalPages
                  ? "pointer-events-none opacity-50"
                  : "cursor-pointer"
              }
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  );
}

